/**
 * Firebase Web Presence module.
 * Manages presence for web clients.
 *
 * @module firebase/presence/web
 */

import {
  ref,
  onDisconnect,
  serverTimestamp,
  set,
  onValue,
  update,
  get,
  remove,
} from "firebase/database";
import { db } from "../config";
import { auth } from "../auth";
import {
  SESSION_TIMING,
  generateConnectionId,
  calculateSessionStatus,
  parseFirebaseTimestamp,
} from "../utils";
import { recalculateAdminGlobalStatus, calculateGlobalStatus } from "./core";
import { monitorMobileAppSessions } from "./mobile";
import {
  stopGlobalMonitoring as stopGlobalMonitoringInternal,
  monitorAllAdminSessions,
} from "./global";

let adminPresenceInterval = null;
let activityTimeout = null;
let offlineTimeout = null;
let lastActivity = Date.now();
let connectionId = null;
let isSetup = false;
let mobileSessionsCleanup = null;

/**
 * Updates the timestamp of the last user activity
 */
const updateLastActivity = () => {
  lastActivity = Date.now();
};

/**
 * Sets up activity event listeners for the document
 */
const setupActivityListeners = () => {
  const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart"];
  events.forEach((event) => {
    document.addEventListener(event, updateLastActivity, { passive: true });
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      updateLastActivity();
      if (auth.currentUser && connectionId) {
        const adminId = auth.currentUser.uid;
        const sessionRef = ref(
          db,
          `admins/${adminId}/sessions/web/${connectionId}`
        );
        update(sessionRef, {
          status: "online",
          lastActive: serverTimestamp(),
        })
          .then(() => {
            recalculateAdminGlobalStatus(adminId);
          })
          .catch((error) => {
            console.error("Error updating status on visibility change:", error);
          });
      }
    }
  });
};

/**
 * Updates the admin's status based on their activity and visibility state
 *
 * @async
 * @param {string} adminId - The ID of the admin
 * @returns {Promise<void>}
 */
const updateAdminStatus = async (adminId) => {
  if (!auth.currentUser || !connectionId) return;

  try {
    const sessionRef = ref(
      db,
      `admins/${adminId}/sessions/web/${connectionId}`
    );
    const sessionSnapshot = await get(sessionRef);
    const currentSession = sessionSnapshot.val() || {};

    const now = Date.now();
    const lastActiveTime = currentSession.lastActive
      ? parseFirebaseTimestamp(currentSession.lastActive)
      : 0;
    const needsHeartbeat =
      now - lastActiveTime > SESSION_TIMING.HEARTBEAT_INTERVAL;

    let newStatus;

    if (document.visibilityState === "visible") {
      newStatus = "online";
    } else {
      const inactiveTime = now - lastActivity;
      newStatus = calculateSessionStatus(inactiveTime);
    }

    if (newStatus !== currentSession.status || needsHeartbeat) {
      await update(sessionRef, {
        status: newStatus,
        lastActive: serverTimestamp(),
        connectionId,
      });
      await recalculateAdminGlobalStatus(adminId);
    }
  } catch (error) {
    console.error("Error updating admin status:", error);
  }
};

/**
 * Handles the forced logout scenario
 *
 * @async
 * @param {string} adminId - The ID of the admin
 */
const handleForcedLogout = async (adminId) => {
  if (!auth.currentUser || !connectionId) return;

  try {
    const sessionRef = ref(
      db,
      `admins/${adminId}/sessions/web/${connectionId}`
    );
    await remove(sessionRef);

    const forceLogoutRef = ref(
      db,
      `admins/${adminId}/forcedLogout/web/${connectionId}`
    );
    await remove(forceLogoutRef);

    cleanupPresenceResources(adminId);

    const newGlobalStatus = await calculateGlobalStatus(adminId);
    await update(ref(db, `admins/${adminId}`), {
      globalStatus: newGlobalStatus,
      lastUpdate: serverTimestamp(),
    });

    if (newGlobalStatus === "offline") {
      await update(ref(db, "globalPresence/adminIds"), { [adminId]: null });
      await set(ref(db, "adminStatus"), false);
    }

    await auth.signOut();
    alert("This session was terminated by an administrator.");

    try {
      localStorage.setItem("adminForcedLogout", Date.now().toString());
      localStorage.removeItem("adminForcedLogout");
    } catch (e) {
      console.error("Error accessing localStorage:", e);
    }

    window.location.reload();
  } catch (error) {
    console.error("Error handling forced logout:", error);
    alert("Your session was terminated. Please reload the page.");
    window.location.reload();
  }
};

/**
 * Cleans up all presence-related resources
 *
 * @param {string} _adminId - The ID of the admin (unused but kept for API consistency)
 */
const cleanupPresenceResources = (_adminId) => {
  if (adminPresenceInterval) {
    clearInterval(adminPresenceInterval);
    adminPresenceInterval = null;
  }

  if (activityTimeout) {
    clearTimeout(activityTimeout);
    activityTimeout = null;
  }

  if (offlineTimeout) {
    clearTimeout(offlineTimeout);
    offlineTimeout = null;
  }

  const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart"];
  events.forEach((event) => {
    document.removeEventListener(event, updateLastActivity);
  });

  connectionId = null;
  if (typeof window !== "undefined") {
    window.adminConnectionId = null;
  }
  isSetup = false;

  if (mobileSessionsCleanup && typeof mobileSessionsCleanup === "function") {
    mobileSessionsCleanup();
    mobileSessionsCleanup = null;
  }
};

/**
 * Sets up presence tracking for an admin
 *
 * @async
 * @param {string} adminId - The ID of the admin
 * @returns {Promise<void>}
 */
export const setupAdminPresence = async (adminId) => {
  if (isSetup) {
    return;
  }

  try {
    connectionId = generateConnectionId();
    if (typeof window !== "undefined") {
      window.adminConnectionId = connectionId;
    }
    const sessionRef = ref(
      db,
      `admins/${adminId}/sessions/web/${connectionId}`
    );

    const existingSession = await get(sessionRef);

    if (existingSession.exists()) {
      await update(sessionRef, {
        status: "online",
        lastActive: serverTimestamp(),
        lastSeen: new Date().toString(),
      });
    } else {
      setupActivityListeners();
      updateLastActivity();

      const forcedLogoutRef = ref(
        db,
        `admins/${adminId}/forcedLogout/web/${connectionId}`
      );
      onValue(forcedLogoutRef, (snapshot) => {
        const forcedLogout = snapshot.val();
        if (forcedLogout && auth.currentUser) {
          handleForcedLogout(adminId);
        }
      });

      const adminStatusRef = ref(db, "adminStatus");
      const connectedRef = ref(db, ".info/connected");

      onValue(connectedRef, async (snapshot) => {
        if (snapshot.val() === true) {
          try {
            const sessionData = {
              status: "online",
              lastActive: serverTimestamp(),
              connectionId: connectionId,
              userAgent: navigator.userAgent,
              deviceType: /mobile|android|iphone|ipad/i.test(
                navigator.userAgent
              )
                ? "mobile"
                : "desktop",
              startedAt: serverTimestamp(),
              lastSeen: new Date().toString(),
            };

            await set(sessionRef, sessionData);

            const globalStatus = await calculateGlobalStatus(adminId);
            await update(ref(db, `admins/${adminId}`), {
              globalStatus: globalStatus,
              lastUpdate: serverTimestamp(),
            });

            await update(ref(db, "globalPresence/adminIds"), {
              [adminId]: true,
            });

            await set(adminStatusRef, true);

            onDisconnect(sessionRef).remove();

            const adminRef = ref(db, `admins/${adminId}`);
            const disconnectAdminRef = onDisconnect(adminRef);

            const allSessionsSnapshot = await Promise.all([
              get(ref(db, `admins/${adminId}/sessions/web`)),
              get(ref(db, `admins/${adminId}/sessions/mobile`)),
            ]);

            const webSessions = allSessionsSnapshot[0].val() || {};
            const mobileSessions = allSessionsSnapshot[1].val() || {};
            const remainingSessions = {
              ...webSessions,
              ...mobileSessions,
            };

            if (webSessions[connectionId]) {
              delete remainingSessions[connectionId];
            }

            if (Object.keys(remainingSessions).length === 0) {
              onDisconnect(ref(db, "globalPresence/adminIds")).update({
                [adminId]: null,
              });

              onDisconnect(ref(db, "adminStatus")).set(false);

              disconnectAdminRef.update({
                globalStatus: "offline",
                lastUpdate: serverTimestamp(),
              });
            }
          } catch (error) {
            console.error("Error setting up connection state:", error);
          }
        }
      });

      adminPresenceInterval = setInterval(() => {
        updateAdminStatus(adminId);
      }, SESSION_TIMING.HEARTBEAT_INTERVAL);

      const checkActivityStatus = () => {
        const inactiveTime = Date.now() - lastActivity;

        clearTimeout(activityTimeout);
        clearTimeout(offlineTimeout);

        if (inactiveTime < SESSION_TIMING.AWAY_TIMEOUT) {
          activityTimeout = setTimeout(() => {
            updateAdminStatus(adminId);
            checkActivityStatus();
          }, SESSION_TIMING.AWAY_TIMEOUT - inactiveTime);
        } else if (inactiveTime < SESSION_TIMING.OFFLINE_TIMEOUT) {
          updateAdminStatus(adminId);
          offlineTimeout = setTimeout(() => {
            updateAdminStatus(adminId);
            checkActivityStatus();
          }, SESSION_TIMING.OFFLINE_TIMEOUT - inactiveTime);
        } else {
          updateAdminStatus(adminId);
        }
      };

      checkActivityStatus();

      mobileSessionsCleanup = monitorMobileAppSessions(adminId);

      monitorAllAdminSessions();
    }

    isSetup = true;
  } catch (error) {
    console.error("Error setting up admin presence:", error);
  }
};

/**
 * Cleans up presence tracking for an admin
 *
 * @async
 * @param {string} adminId - The ID of the admin
 * @returns {Promise<void>}
 */
export const cleanupAdminPresence = async (adminId) => {
  if (!adminId || !connectionId) {
    return;
  }

  cleanupPresenceResources(adminId);

  try {
    try {
      sessionStorage.removeItem("adminConnectionId");
    } catch (e) {
      console.warn("Could not clear connectionId from sessionStorage:", e);
    }

    const sessionRef = ref(
      db,
      `admins/${adminId}/sessions/web/${connectionId}`
    );
    await remove(sessionRef);

    const forceLogoutRef = ref(
      db,
      `admins/${adminId}/forcedLogout/web/${connectionId}`
    );
    await remove(forceLogoutRef).catch((e) =>
      console.warn("Failed to remove forcedLogout ref:", e)
    );

    const newGlobalStatus = await calculateGlobalStatus(adminId);
    await update(ref(db, `admins/${adminId}`), {
      globalStatus: newGlobalStatus,
      lastUpdate: serverTimestamp(),
    });

    if (newGlobalStatus === "offline") {
      await update(ref(db, "globalPresence/adminIds"), { [adminId]: null });
      await set(ref(db, "adminStatus"), false);
    }
  } catch (error) {
    console.error("Error cleaning up admin presence:", error);
  }
};

export const stopGlobalMonitoring = stopGlobalMonitoringInternal;
